import {
  AppWindow,
  BarChart3,
  BookOpen,
  Boxes,
  CheckSquare,
  GitPullRequestArrow,
  Home,
  Inbox,
  LayoutDashboard,
  PackageOpen,
  Rocket,
  Settings,
  ShieldAlert,
  Ticket,
  UserCog,
  type LucideIcon,
} from 'lucide-react';
import { ADMIN_ENTRY_PERMISSIONS } from '../admin/sections';

/**
 * Where things are.
 *
 * One list, read by the sidebar and by the command palette, so the two can
 * never disagree about what exists or who may see it. Filtering by permission
 * here is a courtesy — the server refuses regardless — but a link that only
 * leads to "you can't do that" is a dead end, and hiding it is kinder.
 */

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: string;
  /**
   * Shown when the holder has *any* of these, for sections that aggregate
   * several areas. Administration is reachable by someone who can only read the
   * audit log, and hiding it behind the settings permission would strand them.
   */
  anyPermission?: readonly string[];
  /** Match only the exact path, for the root. */
  end?: boolean;
  /**
   * Addresses under this item's own prefix that belong to a different item.
   * People lives at /admin/people, so without this Administration lit up
   * beside it whenever People was open.
   */
  except?: string[];
  /** Extra words the command palette should match on. */
  keywords?: string[];
}

/**
 * Whether a link is the one for this address.
 *
 * Worked out here rather than by NavLink, whose prefix match cannot be told
 * that one address under its path belongs to somebody else.
 */
export function isNavItemActive(item: NavItem, pathname: string): boolean {
  const under = (base: string) => pathname === base || pathname.startsWith(base.endsWith('/') ? base : `${base}/`);
  if (item.end ? pathname !== item.to : !under(item.to)) return false;
  return !(item.except ?? []).some(under);
}

export interface NavGroup {
  /** Omitted for the first group, which needs no heading. */
  label?: string;
  items: NavItem[];
}

/**
 * Any one of these opens at least one administration section.
 *
 * Taken from the hub's own list of sections rather than copied here. A copy
 * drifted twice: first to six of the gates, so a role covering only email,
 * reply templates or single sign-on had no way to find the screens it was
 * given; then, once corrected, again as soon as the hub grew System,
 * Departments and Locations. The sections gate the catalogue and asset types
 * at the level that can change them — every requester can *view* the
 * catalogue, and an "Administration" link leading them to a read-only copy of
 * it would only be noise.
 */
export const ADMIN_PERMISSIONS: readonly string[] = ADMIN_ENTRY_PERMISSIONS;

/**
 * An agent's map: the queue first, then the practices that hang off it, then
 * the reference material, then configuration. Grouped because fifteen flat
 * links is a list to read, and five labelled groups is a map to glance at.
 */
export const AGENT_NAV: NavGroup[] = [
  {
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true, keywords: ['home', 'overview'] },
      { to: '/tickets', label: 'Tickets', icon: Ticket, permission: 'ticket.view', keywords: ['queue', 'incidents', 'requests'] },
      { to: '/approvals', label: 'Approvals', icon: CheckSquare, permission: 'approval.view', keywords: ['approve', 'decide'] },
    ],
  },
  {
    label: 'Service management',
    items: [
      { to: '/problems', label: 'Problems', icon: ShieldAlert, permission: 'problem.view', keywords: ['root cause', 'known error'] },
      { to: '/changes', label: 'Changes', icon: GitPullRequestArrow, permission: 'change.view', keywords: ['cab', 'rfc'] },
      { to: '/releases', label: 'Releases', icon: Rocket, permission: 'release.view', keywords: ['deploy'] },
    ],
  },
  {
    label: 'Resources',
    items: [
      { to: '/catalog', label: 'Service catalog', icon: PackageOpen, permission: 'catalog.view', keywords: ['request', 'order'] },
      { to: '/assets', label: 'Assets', icon: Boxes, permission: 'asset.view', keywords: ['cmdb', 'inventory', 'hardware'] },
      {
        to: '/software',
        label: 'Software',
        icon: AppWindow,
        permission: 'software.view',
        keywords: ['applications', 'apps', 'programs', 'installed', 'licences', 'import'],
      },
      { to: '/knowledge', label: 'Knowledge base', icon: BookOpen, permission: 'kb.view', keywords: ['articles', 'kb', 'help'] },
    ],
  },
  {
    label: 'Insights',
    items: [{ to: '/reports', label: 'Reports', icon: BarChart3, permission: 'report.view', keywords: ['analytics', 'metrics'] }],
  },
  {
    label: 'Manage',
    items: [
      { to: '/admin/people', label: 'People', icon: UserCog, permission: 'user.view', keywords: ['users', 'agents', 'groups'] },
      {
        to: '/admin',
        label: 'Administration',
        icon: Settings,
        anyPermission: ADMIN_PERMISSIONS,
        except: ['/admin/people'],
        keywords: ['settings', 'configuration'],
      },
    ],
  },
];

/**
 * A requester's map is short on purpose. They come to get something fixed or
 * to ask for something, and every link that is not one of those is a link
 * they have to read past. The words are theirs, too: "my requests", not
 * "tickets".
 */
export const REQUESTER_NAV: NavGroup[] = [
  {
    items: [
      { to: '/', label: 'Home', icon: Home, end: true, keywords: ['dashboard', 'help'] },
      { to: '/tickets', label: 'My requests', icon: Inbox, permission: 'ticket.view', keywords: ['tickets', 'issues'] },
      { to: '/catalog', label: 'Request something', icon: PackageOpen, permission: 'catalog.view', keywords: ['catalog', 'order', 'service'] },
      { to: '/knowledge', label: 'Help articles', icon: BookOpen, permission: 'kb.view', keywords: ['knowledge', 'kb', 'how to'] },
      { to: '/approvals', label: 'Approvals', icon: CheckSquare, permission: 'approval.view', keywords: ['approve'] },
    ],
  },
];

/** Things that can be created, for the "New" menu and the command palette. */
export interface CreateAction {
  to: string;
  label: string;
  permission: string;
  icon: LucideIcon;
  /** Only offered to agents; a requester raises a request instead. */
  agentOnly?: boolean;
}

export const CREATE_ACTIONS: CreateAction[] = [
  { to: '/tickets/new', label: 'Ticket', permission: 'ticket.create', icon: Ticket },
  { to: '/problems/new', label: 'Problem', permission: 'problem.create', icon: ShieldAlert, agentOnly: true },
  { to: '/changes/new', label: 'Change', permission: 'change.create', icon: GitPullRequestArrow, agentOnly: true },
  { to: '/releases/new', label: 'Release', permission: 'release.create', icon: Rocket, agentOnly: true },
  { to: '/assets/new', label: 'Asset', permission: 'asset.create', icon: Boxes, agentOnly: true },
  { to: '/knowledge/new', label: 'Knowledge article', permission: 'kb.create', icon: BookOpen, agentOnly: true },
];

export function navFor(isAgent: boolean): NavGroup[] {
  return isAgent ? AGENT_NAV : REQUESTER_NAV;
}

/** The groups with what this person may not open removed, and empty groups dropped. */
export function visibleNav(groups: NavGroup[], can: (permission: string) => boolean): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.anyPermission) return item.anyPermission.some((permission) => can(permission));
        return !item.permission || can(item.permission);
      }),
    }))
    .filter((group) => group.items.length > 0);
}

export function visibleCreateActions(isAgent: boolean, can: (permission: string) => boolean): CreateAction[] {
  return CREATE_ACTIONS.filter((action) => can(action.permission) && (isAgent || !action.agentOnly));
}
